/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { MessageCircle, Send, Mic, Square, Paperclip, Plus, Loader2, User, Info, Bot, AlertTriangle, Search, X, Trash2, Filter, ArrowDownLeft, ArrowUpRight, Ban, UserX, Settings, FlaskConical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime(iso) {
  try { return format(parseISO(iso), 'd MMM HH:mm', { locale: it }); } catch { return ''; }
}

function MediaBubble({ media }) {
  const ct = media.content_type || '';
  if (ct.startsWith('audio')) {
    return <audio controls src={media.url} className="max-w-[240px] h-10" data-testid="wa-audio-player" />;
  }
  if (ct.startsWith('image')) {
    return <img src={media.url} alt="allegato" className="max-w-[220px] rounded-lg" />;
  }
  return (
    <a href={media.url} target="_blank" rel="noreferrer" className="text-xs underline flex items-center gap-1">
      <Paperclip size={12} /> Allegato
    </a>
  );
}

export default function WhatsAppInbox() {
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // wa_number
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sendingAudio, setSendingAudio] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [newText, setNewText] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const openWaConfig = () => navigate('/backoffice/whatsapp/configurazione');

  // Ricerca e filtri
  const [search, setSearch] = useState('');
  const [convFilter, setConvFilter] = useState('all'); // all | unread | attention | aioff
  const [searchScope, setSearchScope] = useState('chats'); // chats | messages
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [msgFilter, setMsgFilter] = useState('all'); // all | in | out | ai
  const [highlightId, setHighlightId] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [deleteConv, setDeleteConv] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/conversations`);
      setConversations(res.data);
    } catch (err) { logger.error('WA conversations error:', err); }
    finally { setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (number) => {
    if (!number) return;
    try {
      const res = await axios.get(`${API}/whatsapp/messages`, { params: { number } });
      setMessages(res.data);
    } catch (err) { logger.error('WA messages error:', err); }
  }, []);

  // Ricerca globale nel testo dei messaggi (debounce)
  useEffect(() => {
    if (searchScope !== 'messages') { setSearchResults(null); return; }
    const q = search.trim();
    if (q.length < 2) { setSearchResults(null); return; }
    const t = setTimeout(() => {
      setSearching(true);
      axios.get(`${API}/whatsapp/search`, { params: { q, direction: msgFilter !== 'all' ? msgFilter : undefined } })
        .then(r => setSearchResults(r.data.messages))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [search, searchScope, msgFilter]);

  const openMessage = (m) => {
    setSelected(m.wa_number);
    setHighlightId(m.id);
    setMsgFilter('all');
  };

  // Scroll al messaggio evidenziato quando arriva dalla ricerca
  useEffect(() => {
    if (!highlightId || messages.length === 0) return;
    const el = document.getElementById(`wa-msg-${highlightId}`);
    if (el) {
      el.scrollIntoView({ block: 'center' });
      const t = setTimeout(() => setHighlightId(null), 3500);
      return () => clearTimeout(t);
    }
  }, [highlightId, messages]);

  const confirmDeleteMessage = async () => {
    if (!deleteMsg) return;
    try {
      await axios.delete(`${API}/whatsapp/messages/${deleteMsg.id}`);
      setMessages(prev => prev.filter(m => m.id !== deleteMsg.id));
      setSearchResults(prev => prev ? prev.filter(m => m.id !== deleteMsg.id) : prev);
      toast.success('Messaggio eliminato dall\'archivio');
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eliminazione fallita');
    } finally { setDeleteMsg(null); }
  };

  const confirmDeleteConversation = async () => {
    if (!selected) return;
    try {
      const res = await axios.delete(`${API}/whatsapp/conversations/${encodeURIComponent(selected)}`);
      toast.success(`Conversazione eliminata (${res.data.deleted} messaggi)`);
      setSelected(null); setMessages([]);
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eliminazione fallita');
    } finally { setDeleteConv(false); }
  };

  const normalize = (v) => (v || '').toString().toLowerCase();
  const q = normalize(search.trim());
  const filteredConversations = conversations.filter(c => {
    if (convFilter === 'unread' && !(c.unread > 0)) return false;
    if (convFilter === 'attention' && !c.needs_attention) return false;
    if (convFilter === 'aioff' && !c.auto_reply_disabled) return false;
    if (searchScope === 'chats' && q) {
      const hay = normalize(`${c.patient_nome} ${c.profile_name} ${c.wa_number} ${c.last_message}`);
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const visibleMessages = messages.filter(m => {
    if (msgFilter === 'in') return m.direction === 'in';
    if (msgFilter === 'out') return m.direction === 'out' && !m.auto_reply;
    if (msgFilter === 'ai') return !!m.auto_reply;
    return true;
  });
  const counts = {
    unread: conversations.filter(c => c.unread > 0).length,
    attention: conversations.filter(c => c.needs_attention).length,
    aioff: conversations.filter(c => c.auto_reply_disabled).length,
  };

  const fetchStatus = useCallback(() => {
    axios.get(`${API}/whatsapp/status`).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations, fetchStatus]);

  useEffect(() => {
    if (!selected) return;
    fetchMessages(selected);
    axios.put(`${API}/whatsapp/read`, { number: selected }).then(() => fetchConversations()).catch(() => {});
    const interval = setInterval(() => fetchMessages(selected), 5000);
    return () => clearInterval(interval);
  }, [selected, fetchMessages, fetchConversations]);

  useEffect(() => {
    // Non scorrere in fondo se stiamo evidenziando un messaggio trovato con la ricerca
    if (highlightId) return;
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, highlightId]);

  const sendText = async (to, body, fromDialog = false) => {
    if (!to || !body.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, { to, body: body.trim() });
      if (fromDialog) {
        setNewDialogOpen(false);
        setNewNumber(''); setNewText('');
        setSelected(to.replace('whatsapp:', ''));
      } else {
        setInput('');
      }
      fetchMessages(to.replace('whatsapp:', ''));
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invio fallito');
    } finally { setSending(false); }
  };

  const sendAudioBlob = async (blob, filename) => {
    if (!selected) return;
    setSendingAudio(true);
    try {
      const fd = new FormData();
      fd.append('to', selected);
      fd.append('file', blob, filename);
      await axios.post(`${API}/whatsapp/send-audio`, fd);
      toast.success('Audio inviato');
      fetchMessages(selected);
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invio audio fallito');
    } finally { setSendingAudio(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : undefined });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (blob.size > 0) sendAudioBlob(blob, 'vocale.webm');
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error('Microfono non disponibile o permesso negato');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleFileUpload = (e) => {
    const f = e.target.files?.[0];
    if (f) sendAudioBlob(f, f.name);
    e.target.value = '';
  };

  const selectedConv = conversations.find(c => c.wa_number === selected);

  const toggleAutoReply = async (enabled) => {
    if (!selected) return;
    try {
      await axios.put(`${API}/whatsapp/chat-settings`, { number: selected, auto_reply_disabled: !enabled });
      setConversations(prev => prev.map(c => c.wa_number === selected ? { ...c, auto_reply_disabled: !enabled } : c));
      toast.success(enabled ? 'Auto-risposta AI attivata per questa chat' : 'Auto-risposta AI disattivata per questa chat');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aggiornamento fallito');
    }
  };

  const clearAttention = async () => {
    if (!selected) return;
    try {
      await axios.put(`${API}/whatsapp/chat-settings`, { number: selected, needs_attention: false });
      setConversations(prev => prev.map(c => c.wa_number === selected ? { ...c, needs_attention: false } : c));
      toast.success('Segnalazione risolta');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aggiornamento fallito');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <PageTitle title="WhatsApp" info={`Messaggi e vocali con i pazienti. Numero dello studio: ${status?.number || '...'}`} />
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              size="icon"
              onClick={openWaConfig}
              className="h-10 w-10 border-[hsl(var(--border))] text-[#1F2A37] hover:bg-[hsl(var(--secondary))] hover:text-[#0F5E66]"
              aria-label="Configurazione WhatsApp"
              title="Configurazione WhatsApp"
              data-testid="wa-settings-gear-button"
            >
              <Settings size={18} />
            </Button>
          )}
          <Button onClick={() => setNewDialogOpen(true)} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="wa-new-message-button">
            <Plus size={15} className="mr-1" /> Nuovo Messaggio
          </Button>
        </div>
      </div>


      {status?.engine === 'twilio' && status?.sandbox && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-800" data-testid="wa-sandbox-banner">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Modalità Sandbox Twilio attiva</p>
            <p className="mt-0.5">I pazienti devono prima inviare il messaggio di join alla sandbox ({status.number}). Configura il webhook nella Twilio Console (Messaging → Try it out → WhatsApp sandbox settings, campo "When a message comes in"): <span className="font-mono break-all">{status.webhook_url}</span></p>
          </div>
        </div>
      )}

      {status?.engine === 'waha' && (
        <div className={`mb-4 rounded-lg border p-3 flex items-start gap-2 text-xs ${
          status.waha_session === 'WORKING' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'
        }`} data-testid="wa-waha-banner">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Motore WAHA attivo — sessione: {status.waha_session === 'WORKING' ? 'collegata ✓' : status.waha_session || 'sconosciuta'}</p>
            {status.waha_session !== 'WORKING' && (
              <p className="mt-0.5">
                La sessione non è collegata: {isAdmin ? (
                  <button type="button" onClick={openWaConfig} className="underline font-semibold hover:text-amber-950" data-testid="wa-waha-banner-open-settings">apri la configurazione WhatsApp</button>
                ) : 'chiedi a un amministratore di aprire la configurazione WhatsApp'}, avvia la sessione e scansiona il QR col telefono dello studio.
              </p>
            )}
          </div>
        </div>
      )}

      {status?.test_mode && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="wa-test-mode-banner">
          <FlaskConical size={18} className="text-amber-700 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900">Modalità test attiva</p>
            <p className="text-xs text-amber-800">
              Vengono elaborati e inviati solo i messaggi da/verso i numeri di test
              {status.test_numbers?.length ? <>: <span className="font-mono">{status.test_numbers.join(', ')}</span></> : <span className="font-semibold"> — la lista è vuota, quindi nessun messaggio viene elaborato</span>}. Tutti gli altri numeri vengono ignorati.
            </p>
          </div>
          {isAdmin && <Button size="sm" variant="outline" className="border-amber-300 text-amber-900 hover:bg-amber-100" onClick={openWaConfig} data-testid="wa-test-mode-open-settings"><Settings size={14} className="mr-1" /> Configurazione</Button>}
        </div>
      )}

      {status && status.sending_enabled === false && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="wa-sending-blocked-banner">
          <Ban size={18} className="text-red-600 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-red-900">Invio WhatsApp bloccato</p>
            <p className="text-xs text-red-800">Nessun messaggio (automatico o manuale) viene inviato finché l'interruttore "Invio messaggi WhatsApp" resta disattivato. La ricezione funziona normalmente.</p>
          </div>
          {isAdmin && <Button size="sm" variant="outline" className="border-red-300 text-red-800 hover:bg-red-100" onClick={openWaConfig} data-testid="wa-blocked-open-settings"><Settings size={14} className="mr-1" /> Configurazione</Button>}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" style={{ minHeight: '60vh' }}>
        {/* Conversazioni */}
        <Card className="overflow-hidden flex flex-col">
          {/* Ricerca + filtri */}
          <div className="p-3 border-b space-y-2 bg-[hsl(var(--secondary))]/40">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={searchScope === 'chats' ? 'Cerca chat per nome o numero...' : 'Cerca nel testo dei messaggi...'} className="pl-8 pr-8 h-9 bg-white" data-testid="wa-search-input" />
              {search && <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37]" aria-label="Pulisci ricerca" data-testid="wa-search-clear"><X size={14} /></button>}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="inline-flex rounded-md border border-[hsl(var(--border))] p-0.5 bg-white text-[11px]">
                <button type="button" onClick={() => setSearchScope('chats')} className={`px-2 py-1 rounded transition-colors ${searchScope === 'chats' ? 'bg-[#238A92] text-white' : 'text-muted-foreground hover:text-[#1F2A37]'}`} data-testid="wa-scope-chats">Chat</button>
                <button type="button" onClick={() => setSearchScope('messages')} className={`px-2 py-1 rounded transition-colors ${searchScope === 'messages' ? 'bg-[#238A92] text-white' : 'text-muted-foreground hover:text-[#1F2A37]'}`} data-testid="wa-scope-messages">Messaggi</button>
              </div>
              <div className="flex flex-wrap gap-1 justify-end">
                {[
                  { key: 'all', label: 'Tutte' },
                  { key: 'unread', label: 'Non lette', n: counts.unread },
                  { key: 'attention', label: 'Operatore', n: counts.attention },
                  { key: 'aioff', label: 'AI off', n: counts.aioff },
                ].map(f => (
                  <button key={f.key} type="button" onClick={() => setConvFilter(f.key)} className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${convFilter === f.key ? 'bg-[#1F2A37] text-white border-[#1F2A37]' : 'bg-white text-muted-foreground border-[hsl(var(--border))] hover:text-[#1F2A37]'}`} data-testid={`wa-filter-${f.key}`}>
                    {f.label}{f.n > 0 ? ` ${f.n}` : ''}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <CardContent className="p-0 max-h-[62vh] overflow-y-auto" data-testid="wa-conversations-list">
            {searchScope === 'messages' && search.trim().length >= 2 ? (
              searching && !searchResults ? (
                <div className="p-4 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
              ) : !searchResults || searchResults.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="wa-search-empty">Nessun messaggio contiene "{search.trim()}".</div>
              ) : (
                <>
                  <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">{searchResults.length} messaggi trovati</p>
                  {searchResults.map(m => (
                    <button key={m.id} type="button" onClick={() => openMessage(m)} className="w-full text-left px-4 py-2.5 border-b hover:bg-[hsl(var(--secondary))] transition-colors" data-testid={`wa-search-result-${m.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold truncate">{m.conversation_name || m.wa_number}</p>
                        <span className="text-[10px] text-muted-foreground flex-shrink-0 inline-flex items-center gap-1">
                          {m.direction === 'in' ? <ArrowDownLeft size={10} /> : <ArrowUpRight size={10} />}{m.auto_reply ? ' AI' : ''} {fmtTime(m.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{m.body || '[Allegato]'}</p>
                    </button>
                  ))}
                </>
              )
            ) : loading ? (
              <div className="p-4 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <MessageCircle size={32} className="mx-auto mb-2 text-[#238A92]" />
                Nessuna conversazione. I messaggi in arrivo dei pazienti appariranno qui.
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground" data-testid="wa-conversations-empty">
                Nessuna chat corrisponde ai filtri.
                <div><Button variant="link" size="sm" onClick={() => { setSearch(''); setConvFilter('all'); }}>Azzera filtri</Button></div>
              </div>
            ) : filteredConversations.map(c => (
              <button
                key={c.wa_number}
                onClick={() => setSelected(c.wa_number)}
                data-testid={`wa-conversation-${c.wa_number}`}
                className={`w-full text-left px-4 py-3 border-b flex items-start gap-3 transition-colors duration-150 ${
                  selected === c.wa_number ? 'bg-[#238A92]/10' : 'hover:bg-[hsl(var(--secondary))]'
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-[#238A92]/15 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-[#0F5E66]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold truncate">{c.patient_nome || c.profile_name || c.wa_number}</p>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {c.needs_attention && (
                        <Badge className="bg-amber-500 text-white text-[10px] px-1.5 flex items-center gap-0.5" data-testid={`wa-attention-badge-${c.wa_number}`}>
                          <AlertTriangle size={9} /> Operatore
                        </Badge>
                      )}
                      {c.unread > 0 && <Badge className="bg-[#238A92] text-white text-[10px] px-1.5">{c.unread}</Badge>}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.last_direction === 'out' ? 'Tu: ' : ''}{c.last_message || '[Allegato]'}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{c.wa_number} — {fmtTime(c.last_at)}{c.auto_reply_disabled ? ' — AI off' : ''}</p>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Chat */}
        <Card className="flex flex-col overflow-hidden">
          {!selected ? (
            <CardContent className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-12">
              Seleziona una conversazione o avvia un nuovo messaggio.
            </CardContent>
          ) : (
            <>
              <div className="px-4 py-3 border-b flex items-center gap-2 bg-[hsl(var(--secondary))]/50 flex-wrap">
                <User size={16} className="text-[#0F5E66]" />
                <p className="text-sm font-semibold">{selectedConv?.patient_nome || selectedConv?.profile_name || selected}</p>
                <span className="text-xs text-muted-foreground">{selected}</span>
                <div className="ml-auto flex items-center gap-3">
                  <div className="inline-flex items-center gap-1 text-[11px]" title="Filtra i messaggi di questa chat">
                    <Filter size={12} className="text-muted-foreground" />
                    {[{ k: 'all', l: 'Tutti' }, { k: 'in', l: 'Ricevuti' }, { k: 'out', l: 'Inviati' }, { k: 'ai', l: 'AI' }].map(f => (
                      <button key={f.k} type="button" onClick={() => setMsgFilter(f.k)} className={`px-1.5 py-0.5 rounded transition-colors ${msgFilter === f.k ? 'bg-[#238A92] text-white' : 'text-muted-foreground hover:text-[#1F2A37]'}`} data-testid={`wa-msg-filter-${f.k}`}>{f.l}</button>
                    ))}
                  </div>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50" title="Elimina tutta la conversazione" onClick={() => setDeleteConv(true)} data-testid="wa-delete-conversation-button">
                      <Trash2 size={14} />
                    </Button>
                  )}
                  {selectedConv && selectedConv.registered === false && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5" title="Numero non presente in anagrafica: nessun messaggio automatico viene inviato" data-testid="wa-unregistered-badge">
                      <UserX size={11} /> Non registrato
                    </span>
                  )}
                  {selectedConv?.needs_attention && (
                    <button
                      onClick={clearAttention}
                      className="text-[11px] font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-full px-2.5 py-1 flex items-center gap-1 transition-colors duration-150"
                      title="Il paziente attende un operatore: clicca per segnare come risolto"
                      data-testid="wa-clear-attention-button"
                    >
                      <AlertTriangle size={11} /> Richiede operatore — risolvi
                    </button>
                  )}
                  <div className="flex items-center gap-1.5" title="Attiva/disattiva la risposta automatica AI per questa conversazione">
                    <Bot size={14} className={selectedConv?.auto_reply_disabled ? 'text-muted-foreground' : 'text-[#0F5E66]'} />
                    <span className="text-[11px] text-muted-foreground hidden sm:inline">Auto-risposta AI</span>
                    <Switch
                      checked={!selectedConv?.auto_reply_disabled}
                      onCheckedChange={toggleAutoReply}
                      className="scale-90"
                      data-testid="wa-chat-autoreply-switch"
                    />
                  </div>
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[52vh]" data-testid="wa-chat-messages">
                {visibleMessages.length === 0 && (
                  <p className="text-center text-xs text-muted-foreground py-8" data-testid="wa-chat-empty">{msgFilter === 'all' ? 'Nessun messaggio in questa conversazione.' : 'Nessun messaggio per il filtro selezionato.'}</p>
                )}
                {visibleMessages.map(m => (
                  <div key={m.id} id={`wa-msg-${m.id}`} className={`group flex items-end gap-1 ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                    {m.direction === 'out' && (
                      <button type="button" onClick={() => setDeleteMsg(m)} className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-muted-foreground hover:text-red-600 p-1 rounded" title="Elimina messaggio" aria-label="Elimina messaggio" data-testid={`wa-delete-msg-${m.id}`}><Trash2 size={13} /></button>
                    )}
                    <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm transition-shadow duration-300 ${highlightId === m.id ? 'ring-2 ring-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,.25)]' : ''} ${
                      m.direction === 'out'
                        ? (m.auto_reply ? 'bg-[#0F5E66] text-white' : 'bg-[#238A92] text-white')
                        : 'bg-[hsl(var(--secondary))] text-[#1F2A37]'
                    }`}>
                      {m.auto_reply && (
                        <p className="text-[10px] font-semibold text-white/80 flex items-center gap-1 mb-0.5" data-testid="wa-ai-label">
                          <Bot size={10} /> Assistente AI
                        </p>
                      )}
                      {(m.media || []).map((med, i) => <div key={`${m.id}-med-${i}`} className="mb-1"><MediaBubble media={med} /></div>)}
                      {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                      <p className={`text-[10px] mt-1 ${m.direction === 'out' ? 'text-white/70' : 'text-muted-foreground'}`}>
                        {fmtTime(m.created_at)}{m.direction === 'out' && m.sent_by && !m.auto_reply ? ` — ${m.sent_by}` : ''}
                      </p>
                    </div>
                    {m.direction === 'in' && (
                      <button type="button" onClick={() => setDeleteMsg(m)} className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-muted-foreground hover:text-red-600 p-1 rounded" title="Elimina messaggio" aria-label="Elimina messaggio" data-testid={`wa-delete-msg-${m.id}`}><Trash2 size={13} /></button>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-3 border-t">
                {recording && (
                  <p className="text-xs text-red-600 mb-2 flex items-center gap-1 animate-pulse">
                    <Mic size={12} /> Registrazione in corso... premi stop per inviare
                  </p>
                )}
                <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); sendText(selected, input); }}>
                  <input ref={fileInputRef} type="file" accept="audio/*" className="hidden" onChange={handleFileUpload} data-testid="wa-audio-file-input" />
                  <Button type="button" variant="outline" size="icon" className="flex-shrink-0" title="Allega file audio"
                    onClick={() => fileInputRef.current?.click()} disabled={sendingAudio} data-testid="wa-attach-audio-button">
                    <Paperclip size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant={recording ? 'destructive' : 'outline'}
                    size="icon"
                    className="flex-shrink-0"
                    title={recording ? 'Ferma e invia' : 'Registra vocale'}
                    onClick={recording ? stopRecording : startRecording}
                    disabled={sendingAudio}
                    data-testid="wa-record-button"
                  >
                    {sendingAudio ? <Loader2 size={16} className="animate-spin" /> : recording ? <Square size={16} /> : <Mic size={16} />}
                  </Button>
                  <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Scrivi un messaggio..." disabled={sending} data-testid="wa-text-input" />
                  <Button type="submit" disabled={sending || !input.trim()} className="bg-[#238A92] hover:bg-[#0F5E66] text-white flex-shrink-0" data-testid="wa-send-button">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </Button>
                </form>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Nuovo messaggio */}
      {/* Conferma eliminazione messaggio */}
      <AlertDialog open={!!deleteMsg} onOpenChange={(o) => { if (!o) setDeleteMsg(null); }}>
        <AlertDialogContent data-testid="wa-delete-msg-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo messaggio?</AlertDialogTitle>
            <AlertDialogDescription>
              Il messaggio verrà rimosso dall'archivio dello studio. Non viene cancellato dal telefono del paziente.
              {deleteMsg?.body && <span className="block mt-2 text-xs italic text-[#1F2A37] line-clamp-3">"{deleteMsg.body}"</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDeleteMessage} data-testid="wa-delete-msg-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conferma eliminazione conversazione */}
      <AlertDialog open={deleteConv} onOpenChange={setDeleteConv}>
        <AlertDialogContent data-testid="wa-delete-conv-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'intera conversazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati definitivamente tutti i {messages.length} messaggi con <strong>{selectedConv?.patient_nome || selectedConv?.profile_name || selected}</strong> ({selected}) dall'archivio dello studio. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDeleteConversation} data-testid="wa-delete-conv-confirm">Elimina conversazione</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Nuovo Messaggio WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Numero destinatario (formato internazionale)</Label>
              <Input value={newNumber} onChange={e => setNewNumber(e.target.value)} placeholder="+39 333 1234567" data-testid="wa-new-number-input" />
            </div>
            <div className="space-y-2">
              <Label>Messaggio</Label>
              <Textarea value={newText} onChange={e => setNewText(e.target.value)} rows={3} placeholder="Scrivi il messaggio..." data-testid="wa-new-text-input" />
            </div>
            {status?.sandbox && (
              <p className="text-xs text-amber-700">Sandbox: il destinatario deve aver fatto join alla sandbox Twilio, altrimenti il messaggio non verrà recapitato.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialogOpen(false)}>Annulla</Button>
            <Button
              disabled={sending || !newNumber.trim() || !newText.trim()}
              onClick={() => sendText(newNumber.replace(/\s+/g, ''), newText, true)}
              className="bg-[#238A92] hover:bg-[#0F5E66] text-white"
              data-testid="wa-new-send-button"
            >
              {sending ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Send size={15} className="mr-1" />} Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
